import html2canvas from "html2canvas";
import moment from "moment";
import { getCorporationData } from "../../services/corporation.js";
import { formattedDate } from "../../components/formatted/FormattedDateAndTime.js";
import { FillingCyclePdfDocument } from "../../components/download/ActivityCycle/FillingCyclePdfDocument.js";
import { getUniqueHistoryCycle } from "./utils/getUniqueHistoryCycle";

export { getUniqueHistoryCycle };

// Even if we don't use char-reports tables here, reuse the same
// html2canvas sanitizing strategy as characterization-2 to avoid
// browser zoom artifacts that make charts blurry in PDFs.
const sanitizeCloneForCapture = (clonedDoc, clonedElement) => {
  clonedDoc.querySelectorAll("*").forEach((el) => {
    if (el.style && el.style.zoom) el.style.zoom = "";
  });

  clonedElement
    .querySelectorAll(
      ".char-reports-table-container, .char-pdf-table-container",
    )
    .forEach((el) => {
      if (el.style) el.style.boxSizing = "border-box";
    });
};

const captureCanvasData = async (element) => {
  if (!element) {
    throw new Error("PDF chart element is not mounted");
  }
  // Render at 2x scale to increase effective DPI in the PDF
  // (canvas plus grand, affiché à 350px de large côté PDF)
  const canvas = await html2canvas(element, {
    allowTaint: true,
    onclone: sanitizeCloneForCapture,
    // 3x scale for sharper labels in PDF
    scale: 3,
  });
  return canvas.toDataURL("image/png");
};

const getCorporationName = async (user) => {
  const response = await getCorporationData(user);
  if (response.status >= 200 && response.status < 400) {
    return response.data.corporation.corporationName;
  }
  return "";
};

const getPdfFilename = async (timeLine, wasteName, i18n, t) => {
  const formattedTime = moment(timeLine._time).format("YYYYMMDD-HHmm");
  return `AK_${t("Compliance_report")}_${wasteName}_${formattedDate(timeLine._time, i18n.language, true)}_${formattedTime}.pdf`;
};

export const extractCollections = (
  superContainerTimeLine,
  collectionData,
  timeLine,
  historyCycle,
) => {
  const normalize = (collection) => ({
    numberOfAnomaly: collection?.numberOfAnomaly,
    totalVolumeSortingError: collection?.totalVolumeSortingError,
    totalWasteVolume: collection?.totalWasteVolume,
    numberOfStars: collection?.numberOfStars,
    historyCycle: collection?.historyCurrent || historyCycle || [],
    collectionFillingLevel: collection?.collectionFillingLevel,
    numberOfObjects: collection?.numberOfObjects,
  });

  if (superContainerTimeLine) {
    const leftSide = superContainerTimeLine.find((i) =>
      i._value_image?.includes("-L.jpeg"),
    );
    const collection =
      collectionData?.[`${leftSide?._measurement}_${leftSide?._time}`];
    if (Array.isArray(collection) && collection.length > 0) {
      return collection.map(normalize);
    }
    return [normalize({ historyCurrent: historyCycle })];
  }

  const collection =
    collectionData?.[`${timeLine._measurement}_${timeLine._time}`];
  if (!collection) {
    return [normalize({ historyCurrent: historyCycle })];
  }
  return [normalize(collection)];
};

const getHistoryCycleForPdf = (historyCycle, selectedImageUrls) => {
  const cycle = historyCycle || [];
  if (selectedImageUrls && selectedImageUrls.size > 0) {
    return [...cycle]
      .filter((item) => selectedImageUrls.has(item.image_url))
      .sort((a, b) => new Date(a._time) - new Date(b._time));
  }
  return getUniqueHistoryCycle(cycle);
};

const buildPdfData = async ({
  collections,
  superContainerTimeLine,
  timeLine,
  controller,
  t,
  formattedColorMaterial,
  getTemporalData,
  imageDimensions,
  reduceImageQuality,
  azureSASTokenToUrl,
  starsCalculation,
  clientRegisteredWeight,
  numberOfPackmat,
  comments,
  selectedImageUrls,
}) => {
  const pdfData = {
    oneImage: false,
    finalData: [],
    collectionFillingLevel: 0,
    numberOfAnomaly: 0,
    numberOfStars: 0,
    contaminants: 0,
    numberOfPackmat,
    uniqueHistoryCycle: [],
    numberOfObjects: 0,
    comments,
    clientRegisteredWeight: undefined,
    totalVolumeSortingError: 0,
    totalWasteVolume: 0,
  };

  for (let i = 0; i < collections.length; i++) {
    const col = collections[i];
    const uniqueHistoryCycle = getHistoryCycleForPdf(
      col.historyCycle,
      selectedImageUrls,
    );

    for (const item of uniqueHistoryCycle) {
      const { wasteObjectsList, materialsTemporalData } =
        await extractTemporalData(
          item,
          i,
          superContainerTimeLine,
          timeLine,
          controller,
          getTemporalData,
          t,
          formattedColorMaterial,
        );

      const dimensions = await imageDimensions(
        azureSASTokenToUrl(item.image_url),
      );
      const compressedImage = await reduceImageQuality(
        azureSASTokenToUrl(item.image_url),
        0.4,
      );

      pdfData.finalData.push({
        ...item,
        imageDimensions: dimensions,
        wasteObjectsList,
        materialsTemporalData,
        compressedImage,
      });
    }

    pdfData.numberOfAnomaly += col.numberOfAnomaly;
    pdfData.numberOfObjects += col.numberOfObjects;
    pdfData.collectionFillingLevel = col.collectionFillingLevel;
    pdfData.totalVolumeSortingError += col.totalVolumeSortingError;
    pdfData.totalWasteVolume += col.totalWasteVolume;
    pdfData.oneImage = pdfData.oneImage || col.historyCycle.length === 1;
    if (i === 1) pdfData.clientRegisteredWeight = clientRegisteredWeight;
    pdfData.uniqueHistoryCycle.push(...uniqueHistoryCycle);
  }

  pdfData.numberOfStars = starsCalculation(
    pdfData.totalVolumeSortingError,
    pdfData.totalWasteVolume,
  );
  pdfData.contaminants =
    pdfData.totalVolumeSortingError > 0
      ? (pdfData.totalVolumeSortingError * 100) / pdfData.totalWasteVolume < 1
        ? "<1"
        : Math.round(
            (pdfData.totalVolumeSortingError * 100) / pdfData.totalWasteVolume,
          )
      : 0;

  return pdfData;
};

const extractTemporalData = async (
  item,
  index,
  superContainerTimeLine,
  timeLine,
  controller,
  getTemporalData,
  t,
  formattedColorMaterial,
) => {
  let wasteObjectsList = [];
  let materialsTemporalData = [];
  const containerId = superContainerTimeLine
    ? superContainerTimeLine[index]._id
    : timeLine._id;

  const response = await getTemporalData(
    { time: item._time },
    containerId,
    controller.signal,
  );
  if (response.status === 200 && response.data.temporalData?.length) {
    const temporal = response.data.temporalData[0];
    wasteObjectsList = temporal.anomaliesObjects;
    if (temporal.typeAnomaly && typeof temporal.typeAnomaly === "object") {
      materialsTemporalData = Object.keys(temporal.typeAnomaly).map((type) => ({
        name: `${t(type)}`,
        value: type,
        nameAndCount: `${t(type)} (${temporal.typeAnomaly[type]})`,
        color: formattedColorMaterial(type),
      }));
    }
  }
  return { wasteObjectsList, materialsTemporalData };
};

export const GenerateAndDownloadFillingCyclePdf = async ({
  refs,
  user,
  superContainerTimeLine,
  collectionData,
  timeLine,
  historyCycle,
  controller,
  t,
  formattedColorMaterial,
  getTemporalData,
  imageDimensions,
  reduceImageQuality,
  azureSASTokenToUrl,
  starsCalculation,
  clientRegisteredWeight,
  numberOfPackmat,
  comments,
  wasteName,
  clientName,
  dropOff,
  collectionFillingLevel,
  i18n,
  selectedImageUrls,
}) => {
  const { chartRef, cycleChartRef, materialChartRef } = refs;

  const chartImgData = await captureCanvasData(chartRef.current);
  const cycleChartImgData = await captureCanvasData(cycleChartRef.current);
  const materialChartImgData = await captureCanvasData(
    materialChartRef.current,
  );

  const corporationName = await getCorporationName(user);

  const collections = extractCollections(
    superContainerTimeLine,
    collectionData,
    timeLine,
    historyCycle,
  );

  const pdfData = await buildPdfData({
    collections,
    superContainerTimeLine,
    timeLine,
    controller,
    t,
    formattedColorMaterial,
    getTemporalData,
    imageDimensions,
    reduceImageQuality,
    azureSASTokenToUrl,
    starsCalculation,
    clientRegisteredWeight,
    numberOfPackmat,
    comments,
    selectedImageUrls,
  });

  const pdfFilename = await getPdfFilename(timeLine, wasteName, i18n, t);

  const pdfContent = (
    <FillingCyclePdfDocument
      timeLine={timeLine}
      dropOff={dropOff}
      corporationName={corporationName}
      collectionFillingLevel={collectionFillingLevel}
      pdfData={{
        ...pdfData,
        chartImgData,
        cycleChartImgData,
        materialChartImgData,
      }}
      clientName={clientName}
      wasteName={wasteName}
    />
  );

  return { pdfContent, pdfFilename };
};
